import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Truck, RefreshCw, Award } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { getProducts, ShopifyProduct } from "@/lib/shopify";
import { ProductSkeletonGrid } from "@/components/sections/ProductSkeleton";
import { ProductQuickPreview } from "@/components/ProductQuickPreview";

const PRODUCTS_CACHE_KEY = 'ctf_products_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedProducts {
  products: ShopifyProduct[];
  timestamp: number;
}

const getCachedProducts = (): ShopifyProduct[] | null => {
  try {
    const cached = localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!cached) return null;
    
    const { products, timestamp }: CachedProducts = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL) {
      localStorage.removeItem(PRODUCTS_CACHE_KEY);
      return null;
    }
    return products;
  } catch {
    return null;
  }
};

const setCachedProducts = (products: ShopifyProduct[]) => {
  try {
    const cached: CachedProducts = { products, timestamp: Date.now() };
    localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
};

export const ProductsEarly = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewProduct, setPreviewProduct] = useState<ShopifyProduct | null>(null);
  const { addItem, setCartOpen } = useCartStore();

  const loadProducts = useCallback(async () => {
    // Check cache first
    const cached = getCachedProducts();
    if (cached && cached.length > 0) {
      setProducts(cached);
      setLoading(false);
      return;
    }

    try {
      const shopifyProducts = await getProducts(10);
      setProducts(shopifyProducts);
      setCachedProducts(shopifyProducts);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load products immediately on mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleAddToCart = (product: ShopifyProduct) => {
    const variant = product.node.variants.edges[0]?.node;
    if (!variant) return;
    
    const cartItem = {
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions
    };
    addItem(cartItem);
    setCartOpen(true); // Open cart drawer instead of modal
  };

  const getBadge = (title: string) => {
    if (title.toLowerCase().includes('2-pack') || title.toLowerCase().includes('2 pack')) {
      return 'Most Popular';
    }
    if (title.toLowerCase().includes('5') || title.toLowerCase().includes('family')) {
      return 'Best Value';
    }
    return null;
  };

  const getTreeSizeSubtitle = (title: string) => {
    if (title.toLowerCase().includes('5') || title.toLowerCase().includes('family')) {
      return '5-Pack — Trees 8ft+';
    }
    if (title.toLowerCase().includes('2')) {
      return '2-Pack — Trees 6-8ft';
    }
    return '1 Bottle — Trees Under 6ft';
  };

  if (loading) {
    return <ProductSkeletonGrid />;
  }

  return (
    <section id="shop-early" className="py-16 md:py-20 bg-background scroll-mt-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <p className="font-cormorant text-3xl md:text-4xl text-foreground font-medium italic">
            A tree so fresh your family will think you cut it down yourself.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {products.map(product => {
            const variant = product.node.variants.edges[0]?.node;
            const badge = getBadge(product.node.title);
            const treeSizeSubtitle = getTreeSizeSubtitle(product.node.title);
            const price = variant ? parseFloat(variant.price.amount) : 0;

            return (
              <Card 
                key={product.node.id} 
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] relative bg-card border border-border"
              >
                {badge && (
                  <Badge className="absolute top-4 right-4 z-10 bg-seasonal text-seasonal-foreground font-inter font-bold text-sm px-3 py-1.5">
                    {badge}
                  </Badge>
                )}
                
                <div 
                  className="aspect-square overflow-hidden bg-background flex items-center justify-center p-6 cursor-pointer"
                  onClick={() => setPreviewProduct(product)}
                >
                  {product.node.images.edges[0]?.node ? (
                    <img 
                      src={`${product.node.images.edges[0].node.url}&width=400`} 
                      alt={product.node.title} 
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      width={337}
                      height={337}
                    />
                  ) : (
                    <div className="w-full h-full bg-muted rounded-2xl flex items-center justify-center">
                      <span className="text-muted-foreground">No image</span>
                    </div>
                  )}
                </div>
                
                <div className="p-6 space-y-4">
                  <div 
                    className="cursor-pointer"
                    onClick={() => setPreviewProduct(product)}
                  >
                    <h3 className="font-cormorant text-2xl font-bold text-foreground hover:text-primary transition-colors">
                      Christmas Tree Food
                    </h3>
                    <p className="text-base text-muted-foreground font-inter">
                      {treeSizeSubtitle}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-primary font-inter mb-2 flex items-center gap-1.5">
                      <Truck className="h-3.5 w-3.5" />
                      FAST & FREE SHIPPING
                    </p>
                    <span className="text-lg font-inter text-foreground font-semibold">
                      ${price.toFixed(2)}
                    </span>
                  </div>
                  
                  <Button 
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-inter font-semibold text-base h-12" 
                    onClick={() => handleAddToCart(product)} 
                    disabled={!variant?.availableForSale}
                  >
                    {variant?.availableForSale ? 'Add to Cart' : 'Out of Stock'}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-10 text-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-5 w-5 text-primary" />
            <span className="text-sm font-inter">30-Day Money-Back Guarantee</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Truck className="h-5 w-5 text-primary" />
            <span className="text-sm font-inter">Free Shipping</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Award className="h-5 w-5 text-primary" />
            <span className="text-sm font-inter">Made in USA</span>
          </div>
        </div>
      </div>

      <ProductQuickPreview
        product={previewProduct}
        isOpen={!!previewProduct}
        onClose={() => setPreviewProduct(null)}
      />
    </section>
  );
};
